import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { isQuantityUnit } from '@shared/helpers/unit.helpers';
import { quantityUnitToMathJSUnit } from '@shared/models/unit.model';

export type ElementListRowDisplayInput = {
  /** Typed/resolved numeric value in {@link typedUnit}. `undefined` means fallback-driven. */
  typedValue: number | undefined;
  /** Raw stored unit for the typed value (e.g. `'m'`). */
  typedUnit: string | undefined;
  /** Cosmetic unit override from the quantity property; disables auto-conversion. */
  customDisplayUnit: string | undefined;
  /** Default unit when nothing else applies (from `elementQuantityUnitRecord`). */
  defaultUnit: string;
  /**
   * Unit returned by {@link useExpressionDisplayValues} when the row is fallback-driven.
   * The hook already auto-converts via {@link getDisplayValueData}, so this is the
   * formatted unit (e.g. `'mm'`) for the resolved fallback value.
   */
  fallbackUnit: string | undefined;
  /** Pre-formatted placeholder string from the hook for the fallback-driven case. */
  fallbackPlaceholder: string;
  /** Converted number represented by {@link ElementListRowDisplayInput.fallbackPlaceholder}. */
  fallbackDisplayNumber?: number;
};

export type ElementListRowDisplay = {
  displayValue: string | undefined;
  /** Numeric value after display-unit conversion, used for animated transitions. */
  displayNumber: number | undefined;
  displayUnit: string;
  placeholder: string | undefined;
};

/**
 * Picks the displayed value/unit/placeholder for an EL row's quantity cell.
 *
 * Two paths:
 * - **Typed value** (user-entered or resolved): auto-converts via
 *   {@link getDisplayValueData} (e.g. `0.35 m` → `350 mm`) unless a
 *   {@link ElementListRowDisplayInput.customDisplayUnit} override is set or the
 *   stored unit is not a real {@link isQuantityUnit}.
 * - **Fallback-driven** (no typed value): trusts the unit already auto-converted
 *   by {@link useExpressionDisplayValues} and renders its placeholder string.
 *   Fixes the case where the EL row used to show `350 m` because it only checked
 *   the typed-value unit and ignored the hook's converted fallback unit.
 */
export const getElementListRowDisplay = (
  input: ElementListRowDisplayInput,
): ElementListRowDisplay => {
  const {
    typedValue,
    typedUnit,
    customDisplayUnit,
    defaultUnit,
    fallbackUnit,
    fallbackPlaceholder,
    fallbackDisplayNumber,
  } = input;

  if (typedValue != null) {
    if (!customDisplayUnit && isQuantityUnit(typedUnit)) {
      const mathJSUnit = quantityUnitToMathJSUnit(typedUnit);
      if (mathJSUnit) {
        const converted = getDisplayValueData({
          value: typedValue,
          unit: mathJSUnit,
        });
        return {
          displayValue: converted.value,
          displayNumber: converted.valueAsNumber,
          displayUnit: converted.unit,
          placeholder: undefined,
        };
      }
    }
    return {
      displayValue: getDisplayNumber(typedValue),
      displayNumber: typedValue,
      displayUnit: customDisplayUnit ?? defaultUnit,
      placeholder: undefined,
    };
  }

  return {
    displayValue: undefined,
    displayNumber: fallbackDisplayNumber,
    displayUnit: customDisplayUnit ?? fallbackUnit ?? defaultUnit,
    placeholder: fallbackPlaceholder || undefined,
  };
};
